import React from 'react';

import { Label } from '@atlas/ui';

const PRODUCT_KEYS = {
  casino: 'bonus_product_casino',
  sport: 'bonus_product_sportsbook',
};

const BONUS_CALCULATION_TYPE = {
  DYNAMIC: 'DYNAMIC',
  MANUAL: 'MANUAL',
};

/**
 * The Bonus value depends on how the bonus amount is calculated
 * (`maxBonusCalculationType`):
 *   MANUAL  -> a fixed sum granted to the client -> formatted as currency
 *   DYNAMIC -> a percentage of the deposit, derived from the deposit multiplier
 *              (depositMultiplier * 100)         -> formatted as "{n}%"
 * When the type is missing (e.g. available/template bonuses that carry no
 * copied settings) we fall back to the legacy heuristic: prefer the percentage,
 * then the amount.
 */
const resolveBonusValue = bonusInfo => {
  if (!bonusInfo) return null;

  const calculationType = String(bonusInfo.calculationType).toUpperCase();
  const amount = bonusInfo.bonusAmount ? { kind: 'amount', value: bonusInfo.bonusAmount } : null;

  if (calculationType === BONUS_CALCULATION_TYPE.MANUAL) return amount;

  if (calculationType === BONUS_CALCULATION_TYPE.DYNAMIC) {
    if (bonusInfo.depositMultiplier == null) return null;

    // depositMultiplier is the deposit-match rate (0.1 -> 10%). Round to 2
    // decimals to drop binary-float noise (0.1 * 100 = 10.000…2).
    const percent = Math.round(bonusInfo.depositMultiplier * 100 * 100) / 100;

    return percent ? { kind: 'percent', value: percent } : null;
  }

  // Unknown/missing calculation type — fall back to the legacy heuristic.
  if (bonusInfo.bonusPercent) return { kind: 'percent', value: bonusInfo.bonusPercent };

  return amount;
};

const formatList = (list, separator = ', ') => (list?.length ? list.join(separator) : null);
export const BONUS_HEADER_KEYS = {
  PRODUCT: 'bonus_info_product',
  WAGER: 'bonus_info_wager',
  MIN_DEPOSIT: 'bonus_info_min_deposit',
  AMOUNT: 'amount',
  BONUS_PERCENT: 'bonus_percent',
};

export const BONUS_COLUMN_IDS = {
  PRODUCT: 'product',
  WAGER: 'wager',
  MIN_DEPOSIT: 'minDeposit',
  BONUS: 'bonus',
};

const BET_TYPE_LABELS = {
  COMPLEX_SYSTEM: 'Complex System',
  LUCKY: 'Lucky',
  MULTIPLE_SET: 'Multiple Set',
  SYSTEM: 'System',
};

export const EntityStatus = {
  ACTIVE: 'ACTIVE',
  IN_PROGRESS: 'IN_PROGRESS',
};

const SPORT_SERVICE_LABELS = {
  LIVE: 'Live',
  PREMATCH: 'Prematch',
};

const formatSportTreeItems = items => {
  if (!items?.length) return null;

  return items
    .map(item => {
      const parts = [];
      if (item.sportName) parts.push(item.sportName);
      if (item.categoryName) parts.push(item.categoryName);
      if (item.eventName) parts.push(item.eventName);
      return parts.join(' / ');
    })
    .join(', ');
};

// Freespin-specific column ids + i18n header keys (FT-8149/8150), extracted to avoid
// magic strings.
const FREESPIN_COLUMN_ID = { MAX_WIN: 'maxWin', MIN_DEPOSIT: 'minDeposit' };
const FREESPIN_COLUMN_HEADER = {
  MAX_WIN: 'additional_terms_max_win',
  MIN_DEPOSIT: 'bonus_info_min_deposit',
};

export const BONUS_INFO_COLUMNS = [
  {
    accessor: ({ bonusInfo }) => bonusInfo?.product,
    cell: product => <Label message={PRODUCT_KEYS[product] ?? PRODUCT_KEYS.casino} />,
    header: BONUS_HEADER_KEYS.PRODUCT,
    id: BONUS_COLUMN_IDS.PRODUCT,
  },
  {
    accessor: ({ bonusInfo }) => bonusInfo?.wager,
    cell: wager => `x${wager}`,
    header: BONUS_HEADER_KEYS.WAGER,
    id: BONUS_COLUMN_IDS.WAGER,
  },
  {
    accessor: ({ bonusInfo }) => bonusInfo?.minDeposit,
    cell: (minDeposit, currencyFormatter) => currencyFormatter.format(minDeposit),
    header: BONUS_HEADER_KEYS.MIN_DEPOSIT,
    id: BONUS_COLUMN_IDS.MIN_DEPOSIT,
  },
  {
    accessor: ({ bonusInfo }) => bonusInfo?.maxBonusAmount,
    cell: (maxBonusAmount, currencyFormatter) => currencyFormatter.format(maxBonusAmount),
    header: 'bonus_info_max_bonus_amount',
    id: 'maxBonusAmount',
  },
  {
    accessor: ({ bonusInfo }) => resolveBonusValue(bonusInfo),
    cell: (bonus, currencyFormatter) =>
      bonus.kind === 'percent' ? `${bonus.value}%` : currencyFormatter.format(bonus.value),
    header: bonus =>
      bonus.kind === 'percent' ? BONUS_HEADER_KEYS.BONUS_PERCENT : BONUS_HEADER_KEYS.AMOUNT,
    id: BONUS_COLUMN_IDS.BONUS,
  },
];

export const BONUS_FREESPIN_INFO_COLUMNS = [
  {
    accessor: ({ bonusInfo }) => bonusInfo?.amount,
    cell: (amount, currencyFormatter) => currencyFormatter.format(amount),
    header: 'bonus_info_fs_amount',
    id: 'fsAmount',
  },
  {
    // Spin COUNT - a plain number, not currency (no formatter).
    accessor: ({ bonusInfo }) => bonusInfo?.count,
    header: 'bonus_info_fs_count',
    id: 'fsCount',
  },
  {
    // Only deposit freespins carry minDeposit (no-deposit -> null -> hidden by resolveVisibleColumns)
    accessor: ({ bonusInfo }) => bonusInfo?.minDeposit,
    cell: (minDeposit, currencyFormatter) => currencyFormatter.format(minDeposit),
    header: FREESPIN_COLUMN_HEADER.MIN_DEPOSIT,
    id: FREESPIN_COLUMN_ID.MIN_DEPOSIT,
  },
];

// Freespin Additional Terms - only "Max win" (per design). maxWin is a monetary cap
// in the freespin currency (freeSpinTotalMaxWin[currency]), formatted via currencyFormatter
// like the other amounts. Hidden until the SDK exposes the field (FT-8533);
// resolveVisibleColumns drops null.
export const BONUS_FREESPIN_TERMS_ROWS = [
  {
    accessor: ({ additionalTerms }) => additionalTerms?.maxWin,
    cell: (maxWin, currencyFormatter) => currencyFormatter.format(maxWin),
    header: FREESPIN_COLUMN_HEADER.MAX_WIN,
    id: FREESPIN_COLUMN_ID.MAX_WIN,
  },
];

export const BONUS_TERMS_ROWS = [
  {
    accessor: ({ additionalTerms }) => additionalTerms?.maxBonusAmount,
    cell: (maxBonusAmount, currencyFormatter) => currencyFormatter.format(maxBonusAmount),
    header: 'additional_terms_max_bonus_amount',
    id: 'maxBonusAmount',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.maxBetAmount || null,
    cell: (maxBetAmount, currencyFormatter) => currencyFormatter.format(maxBetAmount),
    header: 'additional_terms_max_bet_amount',
    id: 'maxBetAmount',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.minWagerAmount || null,
    cell: (minWagerAmount, currencyFormatter) => currencyFormatter.format(minWagerAmount),
    header: 'additional_terms_min_wager_amount',
    id: 'minWagerAmount',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.maxWagerAmount || null,
    cell: (maxWagerAmount, currencyFormatter) => currencyFormatter.format(maxWagerAmount),
    header: 'additional_terms_max_wager_amount',
    id: 'maxWagerAmount',
  },
  {
    accessor: ({ additionalTerms }) => formatList(additionalTerms?.notAllowedPaymentMethods),
    header: 'additional_terms_not_allowed_payment_methods',
    id: 'notAllowedPaymentMethods',
  },
  {
    accessor: ({ additionalTerms }) => formatList(additionalTerms?.notAllowedCountries, ' / '),
    header: 'additional_terms_not_allowed_countries',
    id: 'notAllowedCountries',
  },
];

export const SPORT_BONUS_TERMS_ROWS = [
  {
    accessor: ({ additionalTerms }) => additionalTerms?.maxBonusAmount,
    cell: (maxBonusAmount, currencyFormatter) => currencyFormatter.format(maxBonusAmount),
    header: 'additional_terms_max_bonus_amount',
    id: 'maxBonusAmount',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.minWagerAmount || null,
    cell: (minWagerAmount, currencyFormatter) => currencyFormatter.format(minWagerAmount),
    header: 'additional_terms_min_wager_amount',
    id: 'minWagerAmount',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.maxWagerAmount || null,
    cell: (maxWagerAmount, currencyFormatter) => currencyFormatter.format(maxWagerAmount),
    header: 'additional_terms_max_wager_amount',
    id: 'maxWagerAmount',
  },
  {
    accessor: ({ additionalTerms }) =>
      additionalTerms?.sportTreeLimitType === 'allowed' &&
      additionalTerms?.allowedSportServices?.length
        ? formatSportTreeItems(additionalTerms?.allowedSportServices)
        : null,
    header: 'additional_terms_allowed_sports',
    id: 'allowedSports',
  },
  {
    accessor: ({ additionalTerms }) =>
      additionalTerms?.sportTreeLimitType === 'allowed' &&
      additionalTerms?.allowedSportServices?.length
        ? formatSportTreeItems(additionalTerms?.notAllowedSports)
        : null,
    header: 'additional_terms_not_allowed_sports',
    id: 'notAllowedSports',
  },
  {
    accessor: ({ additionalTerms }) =>
      additionalTerms?.allowedSportServices?.length
        ? additionalTerms?.allowedSportServices
            .map(service => SPORT_SERVICE_LABELS[service] || service)
            .join(', ')
        : null,
    header: 'additional_terms_allowed_sport_service',
    id: 'allowedSportService',
  },
  {
    accessor: ({ additionalTerms }) =>
      additionalTerms?.notAllowedBetTypes?.length
        ? additionalTerms?.notAllowedBetTypes.map(type => BET_TYPE_LABELS[type] || type).join(', ')
        : null,
    header: 'additional_terms_not_allowed_bet_types',
    id: 'notAllowedBetTypes',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.minOddsLimitSingle || null,
    header: 'additional_terms_min_odds_limit',
    id: 'minOddsLimitOfSingle',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.minEventsCountMultiple || null,
    header: 'additional_terms_min_events_count',
    id: 'minEventsCountOfMultiple',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.minCountOfBets || null,
    header: 'additional_terms_min_count_bets',
    id: 'minCountOfBets',
  },
  {
    accessor: ({ additionalTerms }) => additionalTerms?.minTotalOddsMultiple || null,
    header: 'additional_terms_min_total_odds',
    id: 'minTotalOddsMultiple',
  },
];

export const resolveVisibleColumns = (columns, entity, currencyFormatter) => {
  if (!entity) return [];

  return columns.reduce((visible, { accessor, cell, header, id }) => {
    const value = accessor(entity);

    if (value == null) return visible;

    visible.push({
      content: cell ? cell(value, currencyFormatter) : value,
      header: typeof header === 'function' ? header(value) : header,
      id,
    });

    return visible;
  }, []);
};
